import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  Row,
  Table,
} from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import { isEmpty } from "lodash";
import { toast } from "react-toastify";

import SettingsPanel from "../../components/SettingsPanel";
import LoadingSpinner from "../../../../Components/Common/Spinner";
import HandleDateTime from "../../../../Components/Common/HandleDateTime";
import {
  getMyWallet,
  getMyWalletTransactions,
  topUpMyWallet,
  updateMyWallet,
  systemWalletResetResponse,
} from "../../../../store/systemWallet/action";
import { getCustomerCards } from "../../../../store/charges/action";

const money = (v, ccy = "GBP") =>
  v === null || v === undefined || v === ""
    ? "-"
    : new Intl.NumberFormat("en-GB", {
        style: "currency",
        currency: ccy || "GBP",
      }).format(Number(v));

const TXN_TYPE = {
  1: "Top Up",
  2: "Usage",
  3: "Refund",
  4: "Adjustment",
  5: "Promotion",
};
const TXN_STATUS = {
  1: "Pending",
  2: "Completed",
  3: "Failed",
  4: "Reversed",
};

const cardLabel = (c) =>
  `${c.Brand ? c.Brand : "Card"} ****${c.Last4Digits || "----"}${
    Number(c.FlagPrimary) === 1 ? " (Primary)" : ""
  }`;

// Customer-facing wallet panel. Everything here is scoped to the logged-in
// user's OWN company on the server (Company_Id from the token) — the front end
// never sends a company id.
export default function WalletBilling() {
  const dispatch = useDispatch();

  const [form, setForm] = useState({
    LowBalanceThreshold: "",
    AutoTopUpAmount: "",
    FlagAutoTopUpEnabled: false,
    MaxMonthlyTopUpLocal: "",
    MasterPaymentCard_Id: "",
  });
  const [topUp, setTopUp] = useState({ Amount: "", MasterPaymentCard_Id: "" });

  const {
    walletResponse,
    walletLoading,
    txnsResponse,
    txnsLoading,
    updateResponse,
    updateLoading,
    topUpResponse,
    topUpLoading,
    cardsList,
  } = useSelector((state) => ({
    walletResponse: state.SystemWallet?.myWalletData,
    walletLoading: state.SystemWallet?.myWalletDataLoading,
    txnsResponse: state.SystemWallet?.myWalletTransactions,
    txnsLoading: state.SystemWallet?.myWalletTransactionsLoading,
    updateResponse: state.SystemWallet?.myWalletUpdateResponse,
    updateLoading: state.SystemWallet?.myWalletUpdateLoading,
    topUpResponse: state.SystemWallet?.myWalletTopUpResponse,
    topUpLoading: state.SystemWallet?.myWalletTopUpLoading,
    cardsList: state.Charges?.customerCardsList?.masterpaymentcard_list,
  }));

  const refetch = () => {
    dispatch(getMyWallet());
    dispatch(getMyWalletTransactions());
  };

  useEffect(() => {
    refetch();
    dispatch(getCustomerCards());
    return () =>
      dispatch(
        systemWalletResetResponse([
          "myWalletTopUpResponse",
          "myWalletUpdateResponse",
        ])
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const wallet = useMemo(() => {
    const r = walletResponse;
    return r?.mastersystemwallet_data || r?.data || null;
  }, [walletResponse]);

  const transactions = useMemo(() => {
    const r = txnsResponse;
    return r?.mastersystemwallettransaction_list || r?.data || [];
  }, [txnsResponse]);

  const cards = useMemo(
    () => (Array.isArray(cardsList) ? cardsList : []),
    [cardsList]
  );

  const belowThreshold =
    wallet &&
    wallet.LowBalanceThreshold !== null &&
    wallet.LowBalanceThreshold !== "" &&
    Number(wallet.BalanceLocal) < Number(wallet.LowBalanceThreshold);

  // hydrate the settings form from the wallet
  useEffect(() => {
    if (!wallet) return;
    setForm({
      LowBalanceThreshold: wallet.LowBalanceThreshold ?? "",
      AutoTopUpAmount: wallet.AutoTopUpAmount ?? "",
      FlagAutoTopUpEnabled: Number(wallet.FlagAutoTopUpEnabled) === 1,
      MaxMonthlyTopUpLocal: wallet.MaxMonthlyTopUpLocal ?? "",
      MasterPaymentCard_Id: wallet.MasterPaymentCard_Id ?? "",
    });
  }, [wallet]);

  // response handlers
  useEffect(() => {
    if (isEmpty(updateResponse)) return;
    if (updateResponse.status === 200) {
      toast.success(updateResponse.message || "Settings saved");
      refetch();
    } else {
      toast.error(updateResponse.message || "Save failed");
    }
    dispatch(systemWalletResetResponse(["myWalletUpdateResponse"]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateResponse]);

  useEffect(() => {
    if (isEmpty(topUpResponse)) return;
    if (topUpResponse.status === 200) {
      toast.success(topUpResponse.message || "Top-up complete");
      setTopUp({ Amount: "", MasterPaymentCard_Id: "" });
      refetch();
    } else {
      toast.error(topUpResponse.message || "Top-up failed");
    }
    dispatch(systemWalletResetResponse(["myWalletTopUpResponse"]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topUpResponse]);

  const saveSettings = (e) => {
    e.preventDefault();
    dispatch(
      updateMyWallet({
        LowBalanceThreshold:
          form.LowBalanceThreshold === ""
            ? null
            : Number(form.LowBalanceThreshold),
        AutoTopUpAmount:
          form.AutoTopUpAmount === "" ? null : Number(form.AutoTopUpAmount),
        FlagAutoTopUpEnabled: form.FlagAutoTopUpEnabled ? 1 : 0,
        MaxMonthlyTopUpLocal:
          form.MaxMonthlyTopUpLocal === ""
            ? null
            : Number(form.MaxMonthlyTopUpLocal),
        MasterPaymentCard_Id:
          form.MasterPaymentCard_Id === ""
            ? null
            : Number(form.MasterPaymentCard_Id),
      })
    );
  };

  const submitTopUp = (e) => {
    e.preventDefault();
    if (!(Number(topUp.Amount) > 0)) {
      toast.error("Enter a top-up amount");
      return;
    }
    const body = { Amount: Number(topUp.Amount) };
    if (topUp.MasterPaymentCard_Id !== "")
      body.MasterPaymentCard_Id = Number(topUp.MasterPaymentCard_Id);
    dispatch(topUpMyWallet(body));
  };

  return (
    <SettingsPanel
      title="Wallet"
      description="Your prepaid balance for pay-as-you-go services such as SMS. Top up, set auto top-up, and review usage."
    >
      {walletLoading && !wallet ? (
        <LoadingSpinner />
      ) : (
        <>
          {/* Balance header */}
          <Card>
            <CardBody>
              <Row className="align-items-center">
                <Col>
                  <div className="text-muted">Current balance</div>
                  <h2
                    className={`mb-0 ${belowThreshold ? "text-danger" : ""}`}
                  >
                    {money(wallet?.BalanceLocal, wallet?.CurrencyCode)}
                  </h2>
                  <small className="text-muted">
                    {wallet?.CurrencyCode || "GBP"}
                    {belowThreshold ? " · below your low-balance threshold" : ""}
                  </small>
                </Col>
                <Col xs="auto" className="text-end">
                  <div className="text-muted">Auto top-up</div>
                  <div>
                    {Number(wallet?.FlagAutoTopUpEnabled) === 1
                      ? `On · ${money(
                          wallet?.AutoTopUpAmount,
                          wallet?.CurrencyCode
                        )}`
                      : "Off"}
                  </div>
                  {wallet?.LastAutoTopUpDateTime ? (
                    <small className="text-muted">
                      Last: {HandleDateTime(wallet.LastAutoTopUpDateTime)}
                    </small>
                  ) : null}
                </Col>
              </Row>
            </CardBody>
          </Card>

          <Row>
            {/* Manual top-up */}
            <Col lg={6}>
              <Card>
                <CardHeader>
                  <h5 className="mb-0">Top up now</h5>
                </CardHeader>
                <CardBody>
                  <Form onSubmit={submitTopUp}>
                    <FormGroup>
                      <Label>Amount</Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="0.00"
                        value={topUp.Amount}
                        onChange={(e) =>
                          setTopUp((t) => ({ ...t, Amount: e.target.value }))
                        }
                      />
                    </FormGroup>
                    <FormGroup>
                      <Label>Card</Label>
                      <Input
                        type="select"
                        value={topUp.MasterPaymentCard_Id}
                        onChange={(e) =>
                          setTopUp((t) => ({
                            ...t,
                            MasterPaymentCard_Id: e.target.value,
                          }))
                        }
                      >
                        <option value="">Primary card on file</option>
                        {cards.map((c) => (
                          <option key={c.Id} value={c.Id}>
                            {cardLabel(c)}
                          </option>
                        ))}
                      </Input>
                    </FormGroup>
                    <Button color="success" disabled={topUpLoading}>
                      {topUpLoading ? "Charging…" : "Top up now"}
                    </Button>
                    {isEmpty(cards) ? (
                      <div className="text-muted mt-2 small">
                        No saved cards. Add one under the Card List tab first.
                      </div>
                    ) : null}
                  </Form>
                </CardBody>
              </Card>
            </Col>

            {/* Auto top-up settings */}
            <Col lg={6}>
              <Card>
                <CardHeader>
                  <h5 className="mb-0">Auto top-up settings</h5>
                </CardHeader>
                <CardBody>
                  <Form onSubmit={saveSettings}>
                    <FormGroup check className="mb-3">
                      <Input
                        type="checkbox"
                        id="autoTopUp"
                        checked={form.FlagAutoTopUpEnabled}
                        onChange={(e) =>
                          setForm((f) => ({
                            ...f,
                            FlagAutoTopUpEnabled: e.target.checked,
                          }))
                        }
                      />
                      <Label check for="autoTopUp">
                        Automatically top up when my balance runs low
                      </Label>
                    </FormGroup>
                    <FormGroup>
                      <Label>Low balance threshold</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={form.LowBalanceThreshold}
                        onChange={(e) =>
                          setForm((f) => ({
                            ...f,
                            LowBalanceThreshold: e.target.value,
                          }))
                        }
                      />
                    </FormGroup>
                    <FormGroup>
                      <Label>Top-up amount</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={form.AutoTopUpAmount}
                        onChange={(e) =>
                          setForm((f) => ({
                            ...f,
                            AutoTopUpAmount: e.target.value,
                          }))
                        }
                      />
                    </FormGroup>
                    <FormGroup>
                      <Label>Monthly cap (optional)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={form.MaxMonthlyTopUpLocal}
                        onChange={(e) =>
                          setForm((f) => ({
                            ...f,
                            MaxMonthlyTopUpLocal: e.target.value,
                          }))
                        }
                      />
                    </FormGroup>
                    <FormGroup>
                      <Label>Card to charge</Label>
                      <Input
                        type="select"
                        value={form.MasterPaymentCard_Id}
                        onChange={(e) =>
                          setForm((f) => ({
                            ...f,
                            MasterPaymentCard_Id: e.target.value,
                          }))
                        }
                      >
                        <option value="">Primary card on file</option>
                        {cards.map((c) => (
                          <option key={c.Id} value={c.Id}>
                            {cardLabel(c)}
                          </option>
                        ))}
                      </Input>
                    </FormGroup>
                    <Button color="primary" disabled={updateLoading}>
                      {updateLoading ? "Saving…" : "Save settings"}
                    </Button>
                  </Form>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Ledger */}
          <Card>
            <CardHeader>
              <h5 className="mb-0">Usage &amp; top-up history</h5>
            </CardHeader>
            <CardBody>
              {txnsLoading ? (
                <LoadingSpinner />
              ) : isEmpty(transactions) ? (
                <div className="text-muted">No transactions yet.</div>
              ) : (
                <div className="table-responsive">
                  <Table className="align-middle table-nowrap mb-0">
                    <thead className="text-muted">
                      <tr>
                        <th>Date</th>
                        <th>Type</th>
                        <th>Service</th>
                        <th>Description</th>
                        <th>Status</th>
                        <th className="text-end">Amount</th>
                        <th className="text-end">Balance after</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transactions.map((t) => {
                        const credit = t.Direction === "CREDIT";
                        return (
                          <tr key={t.Id}>
                            <td>{HandleDateTime(t.CreatedDateTime)}</td>
                            <td>
                              {t.MasterSystemWalletTransactionType_Name ||
                                TXN_TYPE[
                                  t.MasterSystemWalletTransactionType_Id
                                ] ||
                                "-"}
                            </td>
                            <td>
                              {t.MasterSystemWalletServiceType_Name || "-"}
                            </td>
                            <td>{t.Description || "-"}</td>
                            <td>
                              {t.MasterSystemWalletTransactionStatus_Name ||
                                TXN_STATUS[
                                  t.MasterSystemWalletTransactionStatus_Id
                                ] ||
                                "-"}
                            </td>
                            <td
                              className={`text-end fw-semibold ${
                                credit ? "text-success" : "text-danger"
                              }`}
                            >
                              {credit ? "+" : "-"}
                              {money(
                                Math.abs(Number(t.AmountLocal) || 0),
                                t.CurrencyCode
                              )}
                            </td>
                            <td className="text-end">
                              {money(t.BalanceAfterLocal, t.CurrencyCode)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>
              )}
            </CardBody>
          </Card>
        </>
      )}
    </SettingsPanel>
  );
}
